/**
 * The dojo kit -- the shared furniture every web screen is built from, ported
 * from the mobile app's src/components/Dojo.js.
 *
 * Every measurement here is lifted from that file rather than re-chosen, so the
 * two platforms share one design rather than two interpretations of it. Where a
 * number looks arbitrary it is: it is whatever the phone uses. The mobile GEO
 * block, verbatim: row 46, radius 15, border 2, edge 3, gutter 18.
 *
 * Two deliberate differences from the phone:
 *
 *  - Scale. A phone gives a 30pt title 390pt of width; a browser gives it far
 *    more, so type steps up at `sm` while the proportions between sizes hold.
 *  - Colour comes from useTheme() rather than being written in. The app has a
 *    light mode and a dark mode and the phone has both; a component that hard
 *    codes the dark palette silently breaks one of them.
 */
import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

/** Geometry, from mobile's GEO. */
export const GEO = { row: 46, radius: 15, border: 2, edge: 3, gutter: 18 };

/**
 * Two shaded faces -- bottom and right -- so a control reads as an extruded key
 * rather than a rectangle with a shadow under it. This is the single strongest
 * signature of the mobile design and it is why buttons here are not just
 * rounded rectangles.
 */
export const raised = 'border-2 border-b-4 border-r-[3px]';

/** The press: the key travels down and its bottom face shortens to meet it. */
export const pressable = 'active:translate-y-px active:border-b-2 transition-all';

export const ScreenHeader: React.FC<{
  title: string;
  subtitle?: string;
  right?: React.ReactNode;
  action?: React.ReactNode;
}> = ({ title, subtitle, right, action }) => {
  const { colors } = useTheme();
  return (
    <div className="flex items-center gap-3 px-5 pt-2 pb-4">
      <div className="flex-1 min-w-0">
        {/* Mobile: 30pt / 900 / -0.9 tracking. */}
        <h1 className={`text-[30px] sm:text-[34px] font-black tracking-[-0.9px] truncate ${colors.text}`}>
          {title}
        </h1>
        {subtitle && (
          <p className={`text-[13px] font-bold mt-px truncate ${colors.textSecondary}`}>
            {subtitle}
          </p>
        )}
      </div>
      {right}
      {action}
    </div>
  );
};

/**
 * The four-up figure strip under a screen title. Computed from the whole data
 * set rather than the filtered view, so it states the workload rather than
 * whatever filter happens to be selected -- same rule as the phone.
 */
export const SummaryBand: React.FC<{
  items: Array<{ value: React.ReactNode; label: string; color?: string }>;
}> = ({ items }) => {
  const { colors, isDark } = useTheme();
  return (
    <div className="flex mx-5 mb-[18px]">
      {items.map((it, i) => (
        <div
          key={it.label}
          className={`flex-1 flex flex-col items-center py-0.5 ${
            i < items.length - 1
              ? `border-r ${isDark ? 'border-slate-700/60' : 'border-stone-300/70'}`
              : ''
          }`}
        >
          {/* tabular-nums so the figures do not jitter as they change. */}
          <span
            className={`text-2xl font-black tracking-[-0.8px] tabular-nums ${it.color ? '' : colors.text}`}
            style={it.color ? { color: it.color } : undefined}
          >
            {it.value}
          </span>
          <span
            className={`text-[11.5px] font-bold mt-px uppercase tracking-[0.5px] ${colors.textSecondary}`}
          >
            {it.label}
          </span>
        </div>
      ))}
    </div>
  );
};

/**
 * A filter pill. Mobile: paddingHorizontal 15, paddingVertical 9, fully
 * rounded, 2pt border, 13.5pt/800 label.
 *
 * `highlight` is the phone's treatment for the Assigned tab when something is
 * waiting on you -- it is the one tab that represents another person's request,
 * so it is allowed to draw the eye when the others are not.
 */
export const FilterPill: React.FC<{
  active?: boolean;
  highlight?: boolean;
  count?: number;
  onClick?: () => void;
  children: React.ReactNode;
}> = ({ active, highlight, count, onClick, children }) => {
  const { isDark } = useTheme();

  const base =
    'px-[15px] py-[9px] rounded-full border-2 text-[13.5px] font-extrabold whitespace-nowrap transition-colors';

  // No `dark:` variants anywhere in the kit. tailwind.config.js does not set
  // darkMode, so it defaults to `media` -- those classes would follow the
  // visitor's operating system rather than the app's own theme toggle, and
  // disagree with it the moment someone picks light on a dark machine.
  const state = active
    ? 'bg-dojo-red border-dojo-red text-white'
    : highlight
      ? `bg-dojo-gold/15 border-dojo-gold ${isDark ? 'text-dojo-gold' : 'text-dojo-goldDeep'}`
      : isDark
        ? 'bg-transparent border-slate-700 text-slate-400 hover:text-slate-200 hover:border-slate-500'
        : 'bg-transparent border-stone-300 text-stone-500 hover:text-stone-800 hover:border-stone-400';

  return (
    <button type="button" onClick={onClick} aria-pressed={!!active} className={`${base} ${state}`}>
      {children}
      {typeof count === 'number' && (
        <span className="ml-1.5 tabular-nums opacity-70">{count}</span>
      )}
    </button>
  );
};

/** The primary action button: 48x48 on mobile, with the two shaded faces. */
export const ActionButton: React.FC<{
  onClick?: () => void;
  label: string;
  children: React.ReactNode;
}> = ({ onClick, label, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    title={label}
    className={`w-12 h-12 rounded-[14px] flex items-center justify-center flex-shrink-0
      bg-dojo-red border-dojo-redDeep text-white ${raised} ${pressable}`}
  >
    {children}
  </button>
);

/** The full-width primary button used in forms and modals. */
export const PrimaryButton: React.FC<{
  onClick?: () => void;
  type?: 'button' | 'submit';
  disabled?: boolean;
  children: React.ReactNode;
  className?: string;
}> = ({ onClick, type = 'button', disabled, children, className = '' }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={`rounded-[14px] bg-dojo-red border-dojo-redDeep text-white ${raised} ${pressable}
      px-5 pt-[13px] pb-[11px] text-[15px] font-extrabold disabled:opacity-60 ${className}`}
  >
    {children}
  </button>
);

/** Its quiet counterpart -- same geometry, theme surface, no colour. */
export const SecondaryButton: React.FC<{
  onClick?: () => void;
  disabled?: boolean;
  children: React.ReactNode;
  className?: string;
}> = ({ onClick, disabled, children, className = '' }) => {
  const { colors, isDark } = useTheme();
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`rounded-[14px] ${colors.inputBg} ${isDark ? 'border-slate-600' : 'border-stone-300'}
        ${colors.text} ${raised} ${pressable}
        px-5 pt-[13px] pb-[11px] text-[15px] font-extrabold disabled:opacity-60 ${className}`}
    >
      {children}
    </button>
  );
};

/** A labelled form field, matching the login screen's treatment. */
export const Field: React.FC<{
  label: string;
  children: React.ReactNode;
  className?: string;
}> = ({ label, children, className = '' }) => {
  const { colors } = useTheme();
  return (
    <label className={`block ${className}`}>
      <span className={`block text-[13px] font-extrabold mb-2 ${colors.text}`}>{label}</span>
      {children}
    </label>
  );
};

/** Shared input chrome, so every field on every screen matches. */
export const useInputClass = () => {
  const { colors, isDark } = useTheme();
  return `w-full rounded-[14px] border-2 ${colors.inputBg} ${colors.text}
    ${isDark ? 'border-slate-600 placeholder:text-slate-500' : 'border-stone-300 placeholder:text-stone-400'}
    px-4 py-[13px] text-[15px] focus:outline-none focus:border-dojo-red transition-colors`;
};

/**
 * A surface. Mobile's ThemedCard: 2pt border with a heavier bottom face, 15px
 * radius. `onClick` makes it a real button rather than a div with a handler,
 * so it is reachable by keyboard.
 */
export const Card: React.FC<{
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
}> = ({ children, onClick, className = '' }) => {
  const { colors, isDark } = useTheme();
  const base = `rounded-[15px] border-2 border-b-[3px] ${colors.card} ${colors.cardBorder} ${className}`;

  if (!onClick) return <div className={base}>{children}</div>;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`${base} w-full text-left ${pressable} focus:outline-none focus-visible:border-dojo-red
        ${isDark ? 'hover:border-slate-500' : 'hover:border-stone-400'}`}
    >
      {children}
    </button>
  );
};

/** A section heading. Mobile: 17pt / 800 / -0.3 tracking. */
export const SectionTitle: React.FC<{
  children: React.ReactNode;
  right?: React.ReactNode;
  className?: string;
}> = ({ children, right, className = '' }) => {
  const { colors } = useTheme();
  return (
    <div className={`flex items-center justify-between gap-3 ${className}`}>
      <h2 className={`text-[17px] font-extrabold tracking-[-0.3px] ${colors.text}`}>{children}</h2>
      {right}
    </div>
  );
};

/**
 * The progress ring from the phone's habits widget: one big figure rather than
 * a flat bar, so the dashboard has a focal point instead of every widget
 * competing for attention.
 */
export const ProgressRing: React.FC<{
  percent: number;
  size?: number;
  sublabel?: string;
  color?: string;
}> = ({ percent, size = 84, sublabel, color = '#E34234' }) => {
  const { colors, isDark } = useTheme();
  const pct = Math.min(100, Math.max(0, Math.round(percent)));
  const stroke = Math.max(6, Math.round(size * 0.1));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;

  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90 block" aria-hidden="true">
        <circle
          cx={size / 2} cy={size / 2} r={r}
          stroke={isDark ? '#2B3A44' : '#E2DCD0'} strokeWidth={stroke} fill="none"
        />
        {pct > 0 && (
          <circle
            cx={size / 2} cy={size / 2} r={r}
            stroke={color} strokeWidth={stroke} fill="none"
            strokeDasharray={c} strokeDashoffset={c - (c * pct) / 100} strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 700ms ease-out' }}
          />
        )}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-[19px] font-black tracking-[-0.6px] tabular-nums ${colors.text}`}>
          {pct}%
        </span>
        {sublabel && (
          <span className={`text-[10px] font-bold uppercase tracking-[0.4px] ${colors.textSecondary}`}>
            {sublabel}
          </span>
        )}
      </div>
    </div>
  );
};
